import React, { useEffect, useMemo } from 'react';

import {
  companyTypeOptions,
  crcStatusOptions,
  registrationStatusOptions,
} from './company-report-options';

import { useStipReport } from '../../hooks/use-stip-report';
import { useFetchCompanies } from '../../hooks/fetch/use-fetch-companies';
import { useFetchPersonTypes } from '../../hooks/fetch/use-fetch-person-types';
import { useFetchCompanyTypes } from '../../hooks/fetch/use-fetch-company-types';
import { useCompanyOrPersonFilter } from './useCompanyOrPersonFilter';

import Loading from '../../../../layouts/Loading';

import { GridItem } from '../../components/GridItem';
import { ReportRadioGroup } from '../../components/ReportRadioGroup';
import { ReportDatePicker } from '../../components/ReportDatePicker';
import { ReportMultiSelect } from '../../components/ReportMultiSelect';
import { ReportSectionTitle } from '../../components/ReportSectionTitle';
import { ReportMultiAutocomplete } from '../../components/ReportMultiAutocomplete';
import { useFormikContext } from 'formik';
import { ReportDocumentField } from '../../components/ReportDocumentField';

const CompanySituationReport = () => {
  const { handleRadioChange,handleSelectChange } = useStipReport();
  const { values, setFieldValue } = useFormikContext();

  const { loading: loadingCompanies, companies } = useFetchCompanies({ boWithUsers: true });
  const { loading: loadingCompanyTypes, companyTypes } = useFetchCompanyTypes();
  const { loading: loadingPersonTypes, personTypes } = useFetchPersonTypes();

  const loading = loadingCompanyTypes || loadingPersonTypes || loadingCompanies;

  const { enableCompanies, enablePerson } = useCompanyOrPersonFilter({ personTypes, companyTypes });

  const companiesFilteredByNomeFantasia = useMemo(()=> companies.filter(company => Boolean(company.nmFantasia)), [companies]);

  useEffect(()=> {
    // Para não precisar alterar o backend e manter as buscas
    // por razão social e nome fantasia separadas
    const {empresasRazao, empresasFantasia} = values;
    const uniao = empresasRazao.concat(empresasFantasia); // une os arrays
    setFieldValue('empresas', [...new Set(uniao)]); // pega valores únicos dos arrays
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.empresasRazao, values.empresasFantasia])


  if (loading) {
    return <Loading />;
  }

  return (
    <GridItem container spacing={3}>
     <GridItem sm={6}>
        <ReportSectionTitle gutterBottom>Buscar por Razão Social / Nome:</ReportSectionTitle>
        <ReportMultiAutocomplete
          options={companies}
          onSelect={handleSelectChange}
          label="Todas as empresas"
          name="empresasRazao"
          valueProperty="cdEmpresa"
          labelProperty="deRazaoSocial"
          getOptionLabel={option =>
            Boolean(option.deRazaoSocial) ? option.deRazaoSocial : option.nmCompleto
          }
          limitTags={1}
        />
      </GridItem>
      <GridItem sm={6}>
        <ReportSectionTitle gutterBottom>Buscar por nome fantasia:</ReportSectionTitle>
        <ReportMultiAutocomplete
          options={companiesFilteredByNomeFantasia}
          onSelect={handleSelectChange}
          label="Todas as empresas"
          name="empresasFantasia"
          valueProperty="cdEmpresa"
          labelProperty="nmFantasia"
          limitTags={1}
        />
      </GridItem>

      <GridItem>
        <ReportSectionTitle divider>Buscar pelo número do documento:</ReportSectionTitle>
      </GridItem>

      <GridItem sm={3}>
        <ReportDocumentField name="nuDocumentoOperador" label="CPF ou CNPJ do operador" />
      </GridItem>

      <GridItem>
        <ReportSectionTitle divider />
      </GridItem>

      <GridItem>
        <ReportRadioGroup
          label="Tipo de cadastro:"
          options={companyTypeOptions}
          name={'tipoCadastro'}
          onSelect={handleRadioChange}
        />
      </GridItem>

      {enableCompanies && (
        <GridItem sm={6}>
          <>
            <ReportSectionTitle gutterBottom>Modalidade PJ:</ReportSectionTitle>
            <ReportMultiSelect
              options={companyTypes}
              onSelect={handleSelectChange}
              label="Selecione modalidades PJ"
              name="tiposEmpresa"
              valueProperty="cdTipoEmpresa"
              labelProperty="deTipoEmpresa"
            />
          </>
        </GridItem>
      )}

      {enablePerson && (
        <GridItem sm={6}>
          <>
            <ReportSectionTitle gutterBottom>Modalidade PF:</ReportSectionTitle>
            <ReportMultiSelect
              options={personTypes}
              onSelect={handleSelectChange}
              label="Selecione modalidades PF"
              name="tiposPessoaFisica"
              valueProperty="cdTipoPessoaFisica"
              labelProperty="deTipoPessoaFisica"
            />
          </>
        </GridItem>
      )}

      <GridItem>
        <ReportSectionTitle>Filtrar CRC por data de vencimento:</ReportSectionTitle>
      </GridItem>
      <GridItem sm={6}>
        <ReportDatePicker name="dtVencimento.dtInicio" label="Início" />
      </GridItem>
      <GridItem sm={6}>
        <ReportDatePicker name="dtVencimento.dtFim" label="Fim" />
      </GridItem>

      <GridItem>
        <ReportSectionTitle divider>Opções de filtro:</ReportSectionTitle>
      </GridItem>
      <GridItem>
        <ReportRadioGroup
          label="Situação do cadastro:"
          options={registrationStatusOptions}
          name={'situacaoCadastro'}
          onSelect={handleRadioChange}
        />
      </GridItem>
      <GridItem>
        <ReportRadioGroup
          label="Situação do CRC:"
          options={crcStatusOptions}
          name={'situacaoCrc'}
          onSelect={handleRadioChange}
        />
      </GridItem>
    </GridItem>
  );
};


export default CompanySituationReport;
